import toast from 'react-hot-toast';
import store from '../redux/store';
import {
  selected_object,
  setSelectedMeshesForGrouping,
} from '../redux/Slice/gridSlice';
import {
  isAssetRowSelected,
  isLiveSceneObject3D,
  resolveLiveSceneObject,
} from './ThreeJsUtils/assetVisibilityUtils';
import {
  clampSelectionForChildMode,
  glbHierarchyNodeMatchesUuid,
} from '../components/gridpagefunction/MeshExtractation';
import { getActiveParentForChildSelection } from '../pages/Dashboard/GridPage';
import { resolveParticleAnimationRoot } from './particles/particleAnimationUtils';
import { isLightIdSelected } from './ThreeJsUtils/lightHelperUtils';
import { resolveSceneCameraObject } from './ThreeJsUtils/sceneCameraUtils';
import {
  attachMultiSelectionTransform,
  detachMultiSelectionTransform,
  refreshMultiSelectionTransform,
} from './multiSelectionTransform';

/** Same filters as scene raycast selection in GridPage.handleMouseDown */
export function isSelectableSceneObject(obj) {
  if (!obj) return false;
  return (
    obj.type !== 'CameraHelper' &&
    obj.name !== 'DemoCameraHelper' &&
    obj.name !== 'hdriBackgroundSphere' &&
    !obj.userData?.isHdriBackground &&
    obj.visible !== false
  );
}

function addUniqueObject(objects, seen, obj) {
  if (!obj || !isSelectableSceneObject(obj)) return;
  const key = obj.uuid || obj.name;
  if (!key || seen.has(key)) return;
  seen.add(key);
  objects.push(obj);
}

function resolveSceneObjectByIds(scene, ...ids) {
  if (!scene) return null;
  for (const id of ids) {
    if (!id) continue;
    const obj = scene.getObjectByName(id);
    if (obj) return obj;
  }
  return null;
}

/**
 * Collect every scene object that appears in the left-panel asset hierarchy.
 */
export function collectAllHierarchySceneObjects(
  scene,
  state = store.getState()
) {
  const { assets_data, light_data, camera_properties } =
    state?.assets_object ?? {};
  const particles = state?.particles?.particles ?? [];
  const objects = [];
  const seen = new Set();

  const assetTypes = [
    'image',
    'mesh',
    'video',
    'audio',
    'text',
    'hotspot',
    'empty',
  ];
  assetTypes.forEach((type) => {
    (assets_data?.[type] || []).forEach((item) => {
      const obj = resolveLiveSceneObject(scene, item, type);
      addUniqueObject(objects, seen, obj);
    });
  });

  (camera_properties || []).forEach((cam) => {
    const obj = resolveSceneCameraObject(scene, cam);
    addUniqueObject(objects, seen, obj);
  });

  (light_data || []).forEach((light) => {
    const lightId = light.id || light._id;
    const obj =
      resolveSceneObjectByIds(scene, lightId) ||
      scene?.children?.find(
        (child) =>
          child.isLight &&
          (child.name === lightId || child.name === light.name)
      );
    addUniqueObject(objects, seen, obj);
  });

  (particles || []).forEach((particle) => {
    const obj =
      resolveLiveSceneObject(scene, particle, 'particle') ||
      resolveSceneObjectByIds(scene, particle.id, particle.name, particle._id);
    addUniqueObject(objects, seen, obj);
  });

  (state?.assets_object?.allIntersectedObjects ?? []).forEach((obj) => {
    addUniqueObject(objects, seen, obj);
  });

  return objects;
}

export function getSelectableSceneObjects(state = store.getState(), scene) {
  if (scene) {
    return collectAllHierarchySceneObjects(scene, state);
  }
  const objects = state?.assets_object?.allIntersectedObjects ?? [];
  return objects.filter(isSelectableSceneObject);
}

export function clearMultiSelection(dispatch = store.dispatch) {
  detachMultiSelectionTransform();
  dispatch(setSelectedMeshesForGrouping([]));
}

export function getMultiSelection(state = store.getState()) {
  return state?.assets_object?.selectedMeshesForGrouping ?? [];
}

export function resolveHierarchyItemSceneObject(scene, item, type) {
  if (!scene || !item) return null;
  if (type === 'mesh' && item?.object && item?.parentGLB) {
    return clampSelectionForChildMode(
      scene,
      item.object,
      getActiveParentForChildSelection()
    );
  }
  if (type === 'camera') {
    return resolveSceneCameraObject(scene, item);
  }
  if (type === 'light') {
    const lightId = item.id || item._id;
    return (
      resolveSceneObjectByIds(scene, lightId) ||
      scene.children?.find(
        (child) =>
          child.isLight &&
          (child.name === lightId ||
            child.name === item.name)
      ) ||
      null
    );
  }
  return resolveLiveSceneObject(scene, item, type);
}

function objectKey(obj) {
  return obj?.uuid || obj?.name || null;
}

/**
 * Ctrl/Cmd+click: add or remove one scene object from the multi-selection.
 */
export function toggleSceneObjectSelection({
  scene,
  object,
  transformControls,
  onPrimarySelected,
  syncLocalSelection,
} = {}) {
  if (!object || !isSelectableSceneObject(object)) return [];

  const particleRoot = resolveParticleAnimationRoot(object);
  if (particleRoot) {
    object = particleRoot;
  } else if (scene) {
    object = clampSelectionForChildMode(
      scene,
      object,
      getActiveParentForChildSelection()
    );
  }

  if (!object || !isSelectableSceneObject(object)) return [];

  const state = store.getState();
  let current = [...getMultiSelection(state)];
  const primary = state?.assets_object?.select_object;
  const key = objectKey(object);
  const existingIdx = current.findIndex((o) => objectKey(o) === key);

  if (existingIdx >= 0) {
    current.splice(existingIdx, 1);
  } else {
    if (
      current.length === 0 &&
      primary &&
      isLiveSceneObject3D(primary) &&
      isSelectableSceneObject(primary) &&
      objectKey(primary) !== key
    ) {
      current.push(primary);
    }
    current.push(object);
  }

  if (current.length === 0) {
    clearMultiSelection();
    onPrimarySelected?.(null);
    syncLocalSelection?.(null, []);
    return [];
  }

  store.dispatch(setSelectedMeshesForGrouping(current));

  const primaryObj = current[current.length - 1];
  store.dispatch(selected_object(primaryObj));
  onPrimarySelected?.(primaryObj);
  syncLocalSelection?.(primaryObj, current);

  refreshMultiSelectionTransform({
    scene,
    transformControls,
    objects: current,
  });

  return current;
}

/**
 * Ctrl/Cmd+click: add or remove one hierarchy row from the multi-selection.
 */
export function toggleHierarchyItemSelection({
  scene,
  item,
  type,
  transformControls,
  onPrimarySelected,
  syncLocalSelection,
} = {}) {
  const obj = resolveHierarchyItemSceneObject(scene, item, type);
  return toggleSceneObjectSelection({
    scene,
    object: obj,
    transformControls,
    onPrimarySelected,
    syncLocalSelection,
  });
}

function objectMatchesHierarchyItem(obj, { item, type, scene }) {
  if (!obj || !item) return false;

  const itemIds = [
    item._id,
    item.id,
    item.url,
    item.name,
    item.asset_file_name,
    item.file_name,
    item.temp_file_name,
    item.object?.name,
  ].filter(Boolean);

  const itemObj = resolveSceneObjectByIds(scene, ...itemIds);

  if (itemObj && (obj === itemObj || obj.uuid === itemObj.uuid)) return true;

  if (type === 'light') {
    const lightId = item.id || item._id;
    return (
      obj.isLight &&
      (obj.name === lightId ||
        obj.name === item.name ||
        obj.light_type === item.light_type)
    );
  }

  if (type === 'camera') {
    const camId = item._id || item.id;
    return (
      obj.name === camId ||
      obj.name === 'DemoCamera' ||
      obj._id === camId ||
      obj.id === camId
    );
  }

  if (type === 'mesh' && item?.object && item?.parentGLB) {
    return glbHierarchyNodeMatchesUuid(item, obj.uuid);
  }

  return itemIds.some(
    (id) =>
      obj.name === id ||
      obj._id === id ||
      obj.id === id ||
      obj.url === id
  );
}

export function isHierarchyItemMultiSelected(
  selectedMeshes,
  { item, type, scene }
) {
  if (!selectedMeshes?.length || !item) return false;
  return selectedMeshes.some((obj) =>
    objectMatchesHierarchyItem(obj, { item, type, scene })
  );
}

export function isHierarchyRowSelected(
  select_object,
  selectedMeshesForGrouping,
  { item, type, scene },
  selectedLightId = null
) {
  if (
    isHierarchyItemMultiSelected(selectedMeshesForGrouping, {
      item,
      type,
      scene,
    })
  ) {
    return true;
  }

  if (type === 'light' && selectedLightId) {
    if (isLightIdSelected(item, selectedLightId)) return true;
  }

  return isAssetRowSelected(select_object, item, type, scene);
}

export function isMultiObjectSelected(state = store.getState()) {
  return getMultiSelection(state).length > 1;
}

/**
 * Select every object listed in the asset hierarchy.
 */
export function selectAllObjects({
  scene,
  transformControls,
  onPrimarySelected,
  syncLocalSelection,
} = {}) {
  const selectable = getSelectableSceneObjects(store.getState(), scene);

  if (selectable.length === 0) {
    toast.error('No objects to select.');
    return [];
  }

  store.dispatch(setSelectedMeshesForGrouping(selectable));

  const primary = selectable[0];
  store.dispatch(selected_object(primary));
  onPrimarySelected?.(primary);
  syncLocalSelection?.(primary, selectable);

  attachMultiSelectionTransform({
    scene,
    transformControls,
    objects: selectable,
  });

  // Re-apply after React re-render (camera/selection effects must not override pivot).
  queueMicrotask(() => {
    const multi = getMultiSelection();
    if (multi.length > 1) {
      refreshMultiSelectionTransform({
        scene,
        transformControls,
        objects: multi,
      });
    }
  });

  toast.success(
    `Selected ${selectable.length} object${selectable.length === 1 ? '' : 's'} with shared transform. Ctrl+click to deselect.`,
    { id: 'select-all', duration: 2500 }
  );

  return selectable;
}
